/**
 * Secure logger utility that filters sensitive information
 * Replace console.log with logger.debug to prevent accidental exposure
 */

// List of sensitive keys to filter out
const SENSITIVE_KEYS = [
  'token',
  'idToken',
  'accessToken',
  'refreshToken',
  'password',
  'secret',
  'apiKey',
  'auth',
  'authorization',
  'cookie',
  'session',
  'jwt',
  'bearer',
  'credentials'
]

// List of sensitive fields to redact
const SENSITIVE_FIELDS = [
  'email',
  'uid',
  'userId',
  'user_id',
  'phone',
  'phoneNumber',
  'displayName',
  'photoURL',
  'profilePhoto'
]

/**
 * Deep clone and sanitize an object, removing sensitive data
 */
function sanitizeObject(obj: any, depth = 0): any {
  // Prevent infinite recursion
  if (depth > 10) return '[Max depth reached]'
  
  if (obj === null || obj === undefined) return obj
  
  // Handle primitives
  if (typeof obj !== 'object') return obj
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item, depth + 1))
  }
  
  // Handle objects
  const sanitized: any = {}
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const lowerKey = key.toLowerCase()
      
      // Completely remove sensitive keys
      if (SENSITIVE_KEYS.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]'
        continue
      }
      
      // Redact sensitive fields but keep the key
      if (SENSITIVE_FIELDS.some(field => {
        const fieldLower = field.toLowerCase()
        return lowerKey === fieldLower || 
               lowerKey.endsWith(fieldLower) ||
               lowerKey.includes(fieldLower)
      })) {
        if (typeof obj[key] === 'string' && obj[key].length > 0) {
          // Keep first 3 chars for debugging
          sanitized[key] = obj[key].substring(0, 3) + '***'
        } else {
          sanitized[key] = '[REDACTED]'
        }
        continue
      }
      
      // Handle nested objects
      if (typeof obj[key] === 'object') {
        sanitized[key] = sanitizeObject(obj[key], depth + 1)
      } else {
        sanitized[key] = obj[key]
      }
    }
  }
  
  return sanitized
}

/**
 * Safe logger that filters sensitive information
 */
export const logger = {
  debug: (...args: any[]) => {
    if (process.env.NODE_ENV === 'production') return
    
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'object') {
        return sanitizeObject(arg)
      }
      return arg
    })
    
    console.log(...sanitizedArgs)
  },
  
  info: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'object') {
        return sanitizeObject(arg)
      }
      return arg
    })
    
    console.info(...sanitizedArgs)
  },
  
  warn: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'object') {
        return sanitizeObject(arg)
      }
      return arg
    })
    
    console.warn(...sanitizedArgs)
  },
  
  error: (...args: any[]) => {
    const sanitizedArgs = args.map(arg => {
      if (typeof arg === 'object') {
        return sanitizeObject(arg)
      }
      return arg
    })
    
    console.error(...sanitizedArgs)
  }
}

// For development only - bypasses filtering
export const unsafeLog = (...args: any[]) => {
  if (process.env.NODE_ENV !== 'development') return
  console.log('[UNSAFE LOG - DEV ONLY]', ...args)
}